/**
 * API Client for FIDO2 Backend Services
 *
 * Provides methods to communicate with the FIDO2 backend for JWE token
 * verification. The SDK works in standalone mode - no initialization needed.
 *
 * @module core/api
 */

import { SDKConfig } from '../types/config';

/**
 * Error response from API
 */
export interface APIErrorResponse {
  /**
   * Error message from the server
   */
  message?: string;

  /**
   * Error code from the server
   */
  code?: string;

  /**
   * Additional error details
   */
  details?: unknown;
}

/**
 * Response from the verify endpoint
 */
export interface VerifyResponse {
  /**
   * Indicates if verification was successful
   */
  success: boolean;

  /**
   * Verified user data (if successful)
   */
  userData?: unknown;

  /**
   * Verification result details
   */
  result?: unknown;

  /**
   * Additional verification data
   */
  data?: unknown;
}

/**
 * Custom error class for API-related errors
 */
export class APIError extends Error {
  /**
   * HTTP status code
   */
  public readonly status: number;

  /**
   * Error response from the server
   */
  public readonly response?: APIErrorResponse;

  /**
   * Creates a new APIError
   *
   * @param message - Error message
   * @param status - HTTP status code
   * @param response - Error response from the server
   */
  constructor(message: string, status: number, response?: APIErrorResponse) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.response = response;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, APIError);
    }
  }
}

/**
 * Maps an HTTP status from the verify endpoint to an actionable message.
 *
 * @param status - HTTP status code
 * @param statusText - HTTP status text, used for statuses without a specific message
 * @returns Human-readable error message
 */
function describeVerifyFailure(status: number, statusText: string): string {
  switch (status) {
    case 400:
      return 'Verification failed: Invalid request or malformed JWE token';
    case 401:
      return 'Verification failed: Unauthorized - check API key';
    case 403:
      return 'Verification failed: Forbidden - insufficient permissions';
    case 404:
      // The path is usually right and the HOST usually wrong. The JWE is minted against the
      // policy served by whichever backend the *authenticator* is configured for, and only
      // that backend can verify it. Pointing the SDK at a different host produces exactly
      // this 404, after registration and the assertion have both succeeded.
      return (
        'Verification failed: Endpoint not found (404). Check the verify URL is ' +
        '/v4/fido2/verify AND that its host matches the backend the authenticator is ' +
        'configured for.'
      );
    case 422:
      return 'Verification failed: Invalid JWE token format';
    case 500:
      return 'Verification failed: Internal server error';
    case 503:
      return 'Verification failed: Service unavailable';
    default:
      return `Verification failed: ${status} ${statusText}`;
  }
}

/**
 * API Client for FIDO2 backend communication
 *
 * This class provides methods to verify JWE tokens with the backend.
 * The SDK works in standalone mode - no initialization endpoint needed.
 *
 * @example
 * ```typescript
 * const config: SDKConfig = {
 *   apiKey: 'your-api-key',
 *   apiEndpoints: {
 *     verify: 'https://api.example.com/verify'
 *   },
 *   authenticators: { ... }
 * };
 *
 * const client = new APIClient(config);
 * const result = await client.verify(jweToken);
 * ```
 */
export class APIClient {
  /**
   * SDK configuration
   */
  private readonly config: SDKConfig;

  /**
   * Creates a new APIClient instance
   *
   * @param config - SDK configuration containing API endpoints and authentication
   *
   * @throws {Error} If config is missing required fields
   */
  constructor(config: SDKConfig) {
    if (!config) {
      throw new Error('APIClient: config is required');
    }

    if (!config.apiEndpoints) {
      throw new Error('APIClient: config.apiEndpoints is required');
    }

    if (!config.apiEndpoints.verify) {
      throw new Error('APIClient: config.apiEndpoints.verify is required');
    }

    this.config = config;
  }

  /**
   * Verifies a JWE token with the backend
   *
   * This method sends a JWE (JSON Web Encryption) token to the verification
   * endpoint for validation. The token typically contains encrypted biometric
   * or authentication data from the FIDO2 authenticator.
   *
   * The verification endpoint validates the token and returns the decrypted
   * user data and verification results.
   *
   * Based on the reference implementation in webauthn.js lines 321-340.
   *
   * @param jweToken - The JWE token to verify (typically from largeBlob)
   * @returns Promise that resolves to the verification response
   *
   * @throws {APIError} If the request fails or returns an error status
   * @throws {Error} If jweToken is empty or invalid
   *
   * @example
   * ```typescript
   * const result = await client.verify(jweToken);
   * if (result.success) {
   *   console.log('Verification successful:', result.userData);
   * }
   * ```
   */
  async verify(jweToken: string): Promise<VerifyResponse> {
    // Validate input
    if (typeof jweToken !== 'string') {
      throw new Error('verify: jweToken is required and must be a string');
    }

    if (!jweToken || jweToken.trim().length === 0) {
      throw new Error('verify: jweToken cannot be empty');
    }

    try {
      // Make verification request
      // Based on webauthn.js lines 321-340
      const response = await fetch(this.config.apiEndpoints.verify, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ jwe_token: jweToken })
      });

      // Handle non-OK responses
      if (!response.ok) {
        let errorResponse: APIErrorResponse | undefined;

        try {
          errorResponse = await response.json();
        } catch {
          // Response body is not JSON
        }

        throw new APIError(
          describeVerifyFailure(response.status, response.statusText),
          response.status,
          errorResponse
        );
      }

      // Parse and return response
      return await response.json();
    } catch (error) {
      // Re-throw APIError as-is
      if (error instanceof APIError) {
        throw error;
      }

      // Wrap other errors (network errors, etc.)
      throw new APIError(
        `Verification request failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        0,
        { message: error instanceof Error ? error.message : 'Unknown error' }
      );
    }
  }

  /**
   * Verifies a JWE token with a custom endpoint and API key
   *
   * This method allows verification with any endpoint, supporting the
   * VerificationMode pattern from iOS SDK.
   *
   * @param jweToken - The JWE token to verify
   * @param endpoint - The verification endpoint URL
   * @param apiKey - The API key for authentication
   * @returns Promise that resolves to the verification response
   *
   * @throws {APIError} If the request fails or returns an error status
   * @throws {Error} If jweToken is empty or invalid
   *
   * @example
   * ```typescript
   * // Use custom endpoint
   * const result = await client.verifyWithEndpoint(
   *   jweToken,
   *   'https://api.example.com/verify',
   *   'custom-api-key'
   * );
   * ```
   */
  async verifyWithEndpoint(
    jweToken: string,
    endpoint: string,
    apiKey: string
  ): Promise<VerifyResponse> {
    // Validate input
    if (typeof jweToken !== 'string') {
      throw new Error('verifyWithEndpoint: jweToken is required and must be a string');
    }

    if (!jweToken || jweToken.trim().length === 0) {
      throw new Error('verifyWithEndpoint: jweToken cannot be empty');
    }

    if (!endpoint || typeof endpoint !== 'string') {
      throw new Error('verifyWithEndpoint: endpoint is required and must be a string');
    }

    if (!apiKey || typeof apiKey !== 'string') {
      throw new Error('verifyWithEndpoint: apiKey is required and must be a string');
    }

    try {
      // Make verification request
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'X-API-Key': apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ jwe_token: jweToken })
      });

      // Handle non-OK responses
      if (!response.ok) {
        let errorResponse: APIErrorResponse | undefined;

        try {
          errorResponse = await response.json();
        } catch {
          // Response body is not JSON
        }

        throw new APIError(
          describeVerifyFailure(response.status, response.statusText),
          response.status,
          errorResponse
        );
      }

      // Parse and return response
      return await response.json();
    } catch (error) {
      // Re-throw APIError as-is
      if (error instanceof APIError) {
        throw error;
      }

      // Wrap other errors (network errors, etc.)
      throw new APIError(
        `Verification request failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        0,
        { message: error instanceof Error ? error.message : 'Unknown error' }
      );
    }
  }

  /**
   * Verifies a biometric-proof payload, sending it as the raw request body.
   *
   * Prefer this over {@link verifyWithEndpoint} when you have the `verifyPayload` from an
   * authentication result. The current largeBlob format carries `jwe_token` alongside
   * `device_uuid` and `version`, and the endpoint expects that whole object — wrapping it
   * in another `{ jwe_token: ... }` field double-encodes it and the request fails.
   *
   * Mirrors `verifyJWEPayload` in the native iOS launcher, which assigns the payload
   * directly to the HTTP body.
   *
   * A bare token (legacy blob format) is wrapped as `{ jwe_token }` for compatibility, so
   * callers can pass `verifyPayload` without checking which format they received.
   *
   * @param payload - Raw payload from `AuthenticationResult.verifyPayload`
   * @param endpoint - Verification endpoint URL
   * @param apiKey - API key sent as X-API-Key
   * @returns Promise resolving to the verification response
   *
   * @throws {APIError} If the request fails or returns an error status
   * @throws {Error} If payload is empty
   *
   * @example
   * ```typescript
   * const result = await sdk.authenticate({ domain });
   * if (result.verifyPayload) {
   *   const response = await client.verifyPayload(result.verifyPayload, endpoint, apiKey);
   * }
   * ```
   */
  async verifyPayload(
    payload: string,
    endpoint: string,
    apiKey: string
  ): Promise<VerifyResponse> {
    if (typeof payload !== 'string' || payload.trim().length === 0) {
      throw new Error('verifyPayload: payload is required and must be a non-empty string');
    }

    if (!endpoint || typeof endpoint !== 'string') {
      throw new Error('verifyPayload: endpoint is required and must be a string');
    }

    // Structured payloads go through untouched; a bare token gets the legacy wrapper.
    let body = payload.trim();
    if (!body.startsWith('{')) {
      body = JSON.stringify({ jwe_token: body });
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'X-API-Key': apiKey || '',
          'Content-Type': 'application/json'
        },
        body
      });

      if (!response.ok) {
        let errorResponse: APIErrorResponse | undefined;

        try {
          errorResponse = await response.json();
        } catch {
          // Response body is not JSON
        }

        throw new APIError(
          describeVerifyFailure(response.status, response.statusText),
          response.status,
          errorResponse
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }

      throw new APIError(
        `Verification request failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        0,
        { message: error instanceof Error ? error.message : 'Unknown error' }
      );
    }
  }

  /**
   * Gets the current API configuration
   *
   * @returns The SDK configuration
   */
  getConfig(): Readonly<SDKConfig> {
    return this.config;
  }
}
